<template>
  <div id="purchase-orders" class="align-start fill-height">
    <v-row class="g-container-large" no-gutters>
      <v-col cols="12">
        <h2>{{ pageTitle }} Purchase Orders</h2>

        <v-row>
          <v-col cols="12" sm="4" md="3" lg="2">
            <v-btn
              large
              class="i-btn-select g-full-width"
              :class="{ 'i-btn-select-active': selectedView === DRAFT }"
              @click="setStatusAndFetch(DRAFT)"
            >
              Draft / New
              <v-badge
                v-if="draftCount"
                overlap
                class="ml-1"
                color="orange"
                :content="draftCount"
              >
              </v-badge>
            </v-btn>
          </v-col>
          <v-col cols="12" sm="4" md="3" lg="2">
            <v-btn
              large
              class="i-btn-select g-full-width"
              :class="{
                'i-btn-select-active': selectedView === BEFORE_DELIVERY
              }"
              @click="setStatusAndFetch(BEFORE_DELIVERY)"
            >
              Confirm ETA
              <v-badge
                v-if="awaitingDespatchCount"
                overlap
                class="ml-1"
                color="orange"
                :content="awaitingDespatchCount"
              >
              </v-badge>
            </v-btn>
          </v-col>
          <v-col cols="12" sm="4" md="3" lg="2">
            <v-btn
              large
              class="i-btn-select g-full-width"
              :class="{
                'i-btn-select-active': selectedView === DURING_DELIVERY
              }"
              @click="setStatusAndFetch(DURING_DELIVERY)"
            >
              Confirm Delivered Date
              <v-badge
                v-if="awaitingDeliveryCount"
                overlap
                class="ml-1"
                color="orange"
                :content="awaitingDeliveryCount"
              >
              </v-badge>
            </v-btn>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" md="3" lg="2">
            <i-instructions
              instructionKey="purchase-orders"
              :instructionSection="instructionSection"
            ></i-instructions>
          </v-col>
        </v-row>

        <v-row>
          <template v-if="selectedView === DRAFT">
            <i-draft-orders
              :fetching="fetchingDraftOrders"
              :orders="draftOrders"
            />
          </template>
          <template v-if="selectedView === BEFORE_DELIVERY">
            <i-before-delivery-orders
              :fetching="fetchingFinalAwaitingDespatchOrders"
              :orders="beforeDeliveryOrders"
            />
          </template>
          <template v-if="selectedView === DURING_DELIVERY">
            <i-during-delivery-orders
              :fetching="fetchingFinalAwaitingDeliveryOrders"
              :orders="duringDeliveryOrders"
            />
          </template>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import {
  fetchAwaitingDeliveryFinalOrders,
  fetchAwaitingDespatchFinalOrders,
  fetchDraftOrders
} from '@/services/supplierOrders'
import iDraftOrders from '../components/DraftOrders'
import iBeforeDeliveryOrders from '../components/BeforeDeliveryOrders'
import iDuringDeliveryOrders from '../components/DuringDeliveryOrders'
import iInstructions from '@/components/Instructions'

const VIEW_STATUSES = {
  DRAFT: 'DRAFT',
  BEFORE_DELIVERY: 'BEFORE_DELIVERY',
  DURING_DELIVERY: 'DURING_DELIVERY'
}

export default {
  props: ['status'],

  components: {
    iInstructions,
    iDraftOrders,
    iBeforeDeliveryOrders,
    iDuringDeliveryOrders
  },

  data: () => ({
    fetchingDraftOrders: false,
    fetchingFinalAwaitingDespatchOrders: false,
    fetchingFinalAwaitingDeliveryOrders: false,
    draftOrders: [],
    beforeDeliveryOrders: [],
    duringDeliveryOrders: [],
    selectedView: VIEW_STATUSES.DRAFT,
    ...VIEW_STATUSES
  }),

  computed: {
    ...mapState('purchaseOrders', {
      draftCount: (state) => state.counters?.draftCount,
      awaitingDeliveryCount: (state) => state.counters?.awaitingDeliveryCount,
      awaitingDespatchCount: (state) => state.counters?.awaitingDespatchCount
    }),

    instructionSection() {
      let view = null
      switch (this.selectedView) {
        case VIEW_STATUSES.DRAFT:
          view = 'Draft / New'
          break
        case VIEW_STATUSES.BEFORE_DELIVERY:
          view = 'Confirm ETA'
          break
        case VIEW_STATUSES.DURING_DELIVERY:
          view = 'Confirm Delivered Date'
          break
      }
      return view
    },

    pageTitle() {
      if (this.selectedView === this.DRAFT) {
        return 'Draft / New'
      } else if (this.selectedView === this.BEFORE_DELIVERY) {
        return 'Confirm ETA'
      } else if (this.selectedView === this.DURING_DELIVERY) {
        return 'Confirm Delivered Date'
      } else {
        return null
      }
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    ...mapActions('purchaseOrders', [
      'updateDraftCount',
      'updateAwaitingDeliveryCount',
      'updateAwaitingDespatchCount'
    ]),

    fetchDraftOrders() {
      this.fetchingDraftOrders = true
      fetchDraftOrders()
        .then((response) => {
          this.draftOrders = response
          this.updateDraftCount(this.draftOrders.length)
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Draft / New Purchase orders could not be loaded. Please try again...'
          })
        })
        .finally(() => {
          this.fetchingDraftOrders = false
        })
    },

    fetchAwaitingDespatchFinalOrders() {
      this.fetchingFinalAwaitingDespatchOrders = true

      fetchAwaitingDespatchFinalOrders()
        .then((response) => {
          this.beforeDeliveryOrders = response.supplierOrders
          this.updateAwaitingDespatchCount(this.beforeDeliveryOrders.length)
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Confirm ETA Purchase orders could not be loaded. Please try again...'
          })
        })
        .finally(() => {
          this.fetchingFinalAwaitingDespatchOrders = false
        })
    },

    fetchAwaitingDeliveryFinalOrders() {
      this.fetchingFinalAwaitingDeliveryOrders = true

      fetchAwaitingDeliveryFinalOrders()
        .then((response) => {
          this.duringDeliveryOrders = response.supplierOrders
          this.updateAwaitingDeliveryCount(this.duringDeliveryOrders.length)
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Confirm Delivered Date Purchase orders could not be loaded. Please try again...'
          })
        })
        .finally(() => {
          this.fetchingFinalAwaitingDeliveryOrders = false
        })
    },

    setStatusAndFetch(status) {
      this.selectedView = status
      switch (status) {
        case VIEW_STATUSES.DRAFT:
          this.fetchDraftOrders()
          break

        case VIEW_STATUSES.BEFORE_DELIVERY:
          this.fetchAwaitingDespatchFinalOrders()
          break

        case VIEW_STATUSES.DURING_DELIVERY:
          this.fetchAwaitingDeliveryFinalOrders()
          break
      }
    }
  },

  mounted() {
    if (this.status) {
      this.setStatusAndFetch(this.status)
    } else {
      // the default without specifying the status is DRAFT, so we only need to fetch those, the others get fetched when changing tabs
      this.fetchDraftOrders()
    }
  },

  watch: {
    draftCount() {
      this.fetchDraftOrders()
    },

    awaitingDespatchCount() {
      this.fetchAwaitingDespatchFinalOrders()
    },

    awaitingDeliveryCount() {
      this.fetchAwaitingDeliveryFinalOrders()
    }
  }
}
</script>

<style lang="scss" scoped></style>
